import { useQuery } from 'data-fetching-client';
import env from 'enviro';
import { gate } from 'hub-http/gates';
import { useIsMobile } from 'pricing-pages-ui-components/hooks/useMedia';
import { GET_FEATURE_FLAGS_QUERY } from '../../../../__generated__/chirp/com/hubspot/buying/rpc/ProductRpc.dfc.new';
import { GET_PUBLIC_FEATURE_FLAGS_QUERY } from '../../../../__generated__/chirp/com/hubspot/buying/rpc/PublicProductRpc.dfc.new';
import { getUtk } from '../../../earlyRequester/earlyRequesterUtils';
import { getBrowserLocale } from '../../../utils/getBrowserLocale';
import { usePublicPricingPageDetails } from '../public/usePublicPricingPageDetails';
import { useIsInAppAuth, usePortalGates } from '../user/useUserInfo';
import { useLocalStorage } from '../utilities/useLocalStorage';
export const useActiveFeatureFlags = ({
  utk,
  browserLocale,
  preferredCountryCode,
  skip
}) => {
  var _data$result$activeFe;
  const isInAppAuth = useIsInAppAuth();

  // For in-app (authenticated) pages: use GET_FEATURE_FLAGS_QUERY from ProductRpc (no parameters needed)
  const inAppQuery = useQuery(GET_FEATURE_FLAGS_QUERY, {
    skip: skip || !isInAppAuth
  });

  // For public pages: use GET_PUBLIC_FEATURE_FLAGS_V2_QUERY from PublicProductRpc (requires locale parameters and utk)
  const publicQuery = useQuery(GET_PUBLIC_FEATURE_FLAGS_QUERY, {
    variables: {
      utk: utk || '',
      browserLocale: browserLocale || '',
      preferredCountryCode
    },
    skip: skip || isInAppAuth || !browserLocale
  });
  const {
    data,
    loading,
    error
  } = isInAppAuth ? inAppQuery : publicQuery;
  return {
    featureFlags: (_data$result$activeFe = data === null || data === void 0 ? void 0 : data.result.activeFeatureFlags) !== null && _data$result$activeFe !== void 0 ? _data$result$activeFe : [],
    loading,
    error
  };
};
const getIsQAProxy = () => window.location.hostname.indexOf('wthubspot') >= 0;

/**
 * Hook to determine if a portal is in the "seats" cohort (not "preseats").
 * Returns true if the "seats" feature flag is active.
 * Returns false if only "preseats" is active, or if neither is active.
 */
export const useIsUngatedForSeats = () => {
  const {
    browserLocale,
    preferredCountryCode
  } = getBrowserLocale();
  const utk = getUtk() || '';
  const {
    featureFlags
  } = useActiveFeatureFlags({
    utk,
    browserLocale,
    preferredCountryCode,
    skip: false
  });

  // If "seats" is included in activeFeatureFlags, return true
  if (featureFlags.includes('seats')) {
    return true;
  }

  // If "preseats" is included in activeFeatureFlags, return false
  if (featureFlags.includes('preseats')) {
    return false;
  }

  // Return false if neither is included
  return false;
};

/**
 * @description A React hook for checking if a portal is ungated for a specific feature gate.
 * Supports QA environment overrides via localStorage for testing purposes.
 * For public pages, uses active feature flags from PublicProductRpc instead of portal gates.
 *
 * @param gateKey - The feature gate key to check - remember to use the `gate` function from hub-http/gates!
 * @returns Boolean indicating whether the portal is ungated for the specified feature
 *
 * ```ts
 * // Create a specific hook for a feature gate
 * export const useIsUngatedForCommerceHub = () => {
 *   const gateKey = gate('SSM:CommerceHub');
 *   return useIsUngated(gateKey);
 * };
 *
 * // Use the specific hook in a component
 * const MyComponent = () => {
 *   const isUngated = useIsUngatedForCommerceHub();
 *
 *   if (isUngated) {
 *     return <NewFeatureComponent />;
 *   }
 *
 *   return <DefaultComponent />;
 * };
 * ```
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const useIsUngated = gateKey => {
  const isAppAuth = useIsInAppAuth();
  const portalGates = usePortalGates();
  const {
    data: publicPricingPageDetails,
    loading: publicPricingPageDetailsLoading
  } = usePublicPricingPageDetails({
    isAppAuth
  });
  const isQA = env.isQa() || getIsQAProxy();
  const [override] = useLocalStorage(`${gateKey}_OVERRIDE`);
  if (override === 'true' && isQA) {
    return true;
  }

  // For public pages, use active feature flags from PublicProductRpc
  if (!isAppAuth) {
    if (publicPricingPageDetailsLoading) {
      return false;
    }
    const activeFeatureFlags = (publicPricingPageDetails === null || publicPricingPageDetails === void 0 ? void 0 : publicPricingPageDetails.activeFeatureFlags) || [];
    return activeFeatureFlags.includes(gateKey);
  }

  // For in-app pages, use portal gates
  return portalGates === null || portalGates === void 0 ? void 0 : portalGates.includes(gateKey);
};
export const useIsUngatedForM1HubPages = () => {
  return useIsUngated(gate('SSM:PricingPageV2:M1HubPages'));
};
export const useIsUngatedForV2PublicHubPages = () => {
  const isMobile = useIsMobile();
  const isInAppAuth = useIsInAppAuth();
  const {
    browserLocale,
    preferredCountryCode
  } = getBrowserLocale();
  const utk = getUtk() || '';
  const isQA = env.isQa() || getIsQAProxy();
  const [override] = useLocalStorage('PRICING_PAGE_V2_PUBLIC_HUB_PAGES_OVERRIDE');
  const {
    featureFlags
  } = useActiveFeatureFlags({
    utk: isInAppAuth ? undefined : utk,
    browserLocale: isInAppAuth ? undefined : browserLocale,
    preferredCountryCode: isInAppAuth ? undefined : preferredCountryCode,
    skip: false
  });

  // Exclude mobile from V2 public hub pages for now
  if (isMobile) {
    return false;
  }
  if (override === 'true' && isQA) {
    return true;
  }

  // Not using gate() helper - this is a public feature flag from PublicProductRpc.activeFeatureFlags,
  // not a portal gate. The string must match the flag name returned by the backend.
  return featureFlags.includes('PRICING_PAGE_V2_PUBLIC_HUB_PAGES');
};